import { createSelector } from '@reduxjs/toolkit';
import { formValueSelector } from 'redux-form';
import moment from 'moment';

import { getAclKindsByService } from 'services/aclKinds';
import createPlanDisplay from 'services/createPlanDisplay';
import { OR_ADMIN } from 'constants/roles';

/**
 * Returns the complete createInstance state
 *
 * @param {object} state - Current state
 * @returns {object} - The entire createInstance state
 */
export const getCreateInstanceState = (state) => state.createInstance;

// BEGIN catalog section
// Recreate selectors here instead of importing from the catalog selectors because
// we don't want selector files importing from each other.

/**
 * Returns the complete catalog state.
 *
 * @param {object} state - Current state
 * @returns {object} - The entire catalog state
 */
export const catalogStateSelector = (state) => state.catalog;

/**
 * Returns the services from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of services in the catalog
 */
export const catalogServicesSelector = createSelector(
  catalogStateSelector,
  (state) => state.servicesCatalog.services,
);

/**
 * Returns the service subtypes from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of service subtypes in the catalog
 */
export const catalogServiceSubtypesSelector = createSelector(
  catalogStateSelector,
  (state) => state.serviceSubtypesCatalog.serviceSubtypes,
);

/**
 * Returns the regions from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of regions in the catalog
 */
export const catalogRegionsSelector = createSelector(
  catalogStateSelector,
  (state) => state.regionsCatalog.regions,
);

/**
 * Returns the versions from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of versions in the catalog
 */
export const catalogVersionsSelector = createSelector(
  catalogStateSelector,
  (state) => state.versionsCatalog.versions,
);

/**
 * Returns the cloud providers from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of cloud providers in the catalog
 */
export const catalogCloudProvidersSelector = createSelector(
  catalogStateSelector,
  (state) => state.providersCatalog.providers,
);

/**
 * Returns the flavors from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of flavors in the catalog
 */
export const catalogFlavorsSelector = createSelector(
  catalogStateSelector,
  (state) => state.flavorsCatalog.flavors,
);

/**
 * Returns the addons from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of addons in the catalog
 */
export const catalogAddonsSelector = createSelector(
  catalogStateSelector,
  (state) => state.addonsCatalog.addons,
);

/**
 * Returns the plans from the catalog
 *
 * @param {object} state - Current state
 * @returns {array} - List of plans in the catalog
 */
export const catalogPlansSelector = createSelector(
  catalogStateSelector,
  (state) => state.plansCatalog.plans,
);

// END catalog section

// BEGIN redux form section

const createFormSelector = formValueSelector('create');

/**
 * Returns the service selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The name of the service that's stored in redux form.
 */
export const selectedServiceSelector = (state) => createFormSelector(state, 'service') || '';

/**
 * Returns the version selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The version of the service that's stored in redux form.
 */
export const selectedVersionSelector = (state) => createFormSelector(state, 'version') || '';

/**
 * Returns the cloud provider selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The cloud provider of the service that's stored in redux form.
 */
export const selectedCloudProviderSelector = (state) =>
  createFormSelector(state, 'cloudProvider') || '';

/**
 * Returns the beta status of the cloud provider selected by the user
 *
 * @param {object} state - Current state
 * @returns {Boolean} - whether or not the cloud provider is in beta status
 */
export const selectedCloudProviderIsBetaSelector = createSelector(
  selectedCloudProviderSelector,
  catalogCloudProvidersSelector,
  (cloudProviderName, cloudProviders) => {
    if (!cloudProviderName) {
      return false;
    }

    const [cloudProviderObject] = cloudProviders.filter(
      (cloudProvider) => cloudProviderName === cloudProvider.name,
    );
    return cloudProviderObject.maturityLevel === 2;
  },
);

/**
 * Returns the region selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The region of the service that's stored in redux form.
 */
export const selectedRegionSelector = (state) => createFormSelector(state, 'region') || '';

/**
 * Returns the service subtype selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The service subtype of the service that's stored in redux form.
 */
export const selectedServiceSubtypeSelector = (state) =>
  createFormSelector(state, 'serviceSubtype') || '';

/**
 * Returns the flavor selected by the user in step one of the create flow.
 *
 * @param {object} state - Current state
 * @returns {object} - The flavor of the service that's stored in redux form.
 */
export const selectedFlavorSelector = createSelector(
  (state) => createFormSelector(state, 'flavor'),
  catalogFlavorsSelector,
  (flavorId, catalogFlavors) => {
    const [theFlavor] = catalogFlavors.filter((catalogFlavor) => catalogFlavor.id === flavorId);
    return theFlavor || {};
  },
);

/**
 * Returns the addons selected by the user in step two of the create flow.
 * In the form state, addons is an object.
 *
 * @param {object} state - Current state
 * @returns {array} - The addons of the service that's stored in redux form.
 */
export const selectedAddonsSelector = createSelector(
  (state) => createFormSelector(state, 'addons'),
  (addonsById) => {
    if (typeof addonsById === 'undefined') {
      return [];
    }
    return Object.values(addonsById);
  },
);

/**
 * Returns the plan selected by the user in step two of the create flow.
 *
 * @param {object} state - Current state
 * @returns {object} - The plan that's stored in redux form.
 */
export const selectedPlanSelector = createSelector(
  (state) => createFormSelector(state, 'plan'),
  catalogPlansSelector,
  (planId, catalogPlans) => {
    const [thePlan] = catalogPlans.filter((catalogPlan) => catalogPlan.id === planId);
    return thePlan || {};
  },
);

/**
 * Returns the firewall IP selected by the user in step three of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The firewall IP that's stored in redux form, along with its name
 */
export const selectedCidrSelector = createSelector(
  (state) => createFormSelector(state, 'cidr'),
  (state) => createFormSelector(state, 'cidrName'),
  (cidr, cidrName) => {
    if (!cidr) {
      return '';
    }
    if (!cidrName) {
      return cidr;
    }
    return `${cidrName} / ${cidr}`;
  },
);

/**
 * Returns instance name entered by the user in step four of the create flow.
 *
 * @param {object} state - Current state
 * @returns {string} - The instance name that's stored in redux form, along with its name
 */
export const enteredNameSelector = (state) => createFormSelector(state, 'name');

// END redux form section

// BEGIN create section
// These rely on data from selectors in the other sections

/**
 * Returns the id of the selected service
 *
 * @param {object} state - Current state
 * @returns {string} - service ID
 */
export const selectedServiceIDSelector = createSelector(
  selectedServiceSelector,
  catalogServicesSelector,
  (serviceName, services) => {
    if (!serviceName) return null;
    const selectedService = services.filter((service) => service.name === serviceName)[0];
    return selectedService.id;
  },
);

/**
 * Returns whether or not a service is in beta
 *
 * @param {object} state - Current state
 * @param {object} props - Current props of the component using the selector;
 * must contain a serviceName prop
 * @returns {bool} - A service's beta status
 */
export const isServiceBetaSelector = createSelector(
  catalogServicesSelector,
  (state, props) => ({ serviceName: props.serviceName, isORv4: props.isORv4 }),
  (services, { serviceName, isORv4 }) => {
    const theService = services.filter((service) => service.name === serviceName)[0];

    // once the ORv4-only service selector for MongoDB is removed, the isORv4 flag no
    // longer needs to be accounted for
    return isORv4 || !theService ? false : theService.maturityLevel === 2;
  },
);

/**
 * Returns the id of the selected service subtype
 *
 * @param {object} state - Current state
 * @returns {string} - service subtype ID
 */
export const selectedServiceSubtypeIDSelector = createSelector(
  selectedServiceSubtypeSelector,
  catalogServiceSubtypesSelector,
  (serviceSubtypeName, serviceSubtypes) => {
    if (!serviceSubtypeName) return null;
    const selectedService = serviceSubtypes.filter(
      (serviceSubtype) => serviceSubtype.name === serviceSubtypeName,
    )[0];
    return selectedService.id;
  },
);

/**
 * Returns a list of service subtypes available for a service
 *
 * @param {object} state - Current state
 * @returns {array} - list of available service subtypes
 */
export const availableServiceSubtypesSelector = createSelector(
  selectedServiceIDSelector,
  catalogServiceSubtypesSelector,
  (serviceID, subtypes) => subtypes.filter((subtype) => subtype.service === serviceID),
);

/**
 * Returns the list of names for the available service subtypes
 *
 * @param {object} state - Current state
 * @returns {array} - list of available service subtype names
 */
export const availableServiceSubtypesNamesSelector = createSelector(
  availableServiceSubtypesSelector,
  (subtypes) => subtypes.map((subtype) => subtype.name),
);

/**
 * Returns a list of regions available for a service subtype
 *
 * @param {object} state - Current state
 * @returns {array} - list of available regions
 */
export const availableRegionsSelector = createSelector(
  selectedServiceSubtypeIDSelector,
  catalogRegionsSelector,
  (serviceSubtypeID, regions) =>
    regions.filter(
      (region) => region.serviceSubtypes.includes(serviceSubtypeID) && region.displaySetting === 1,
    ),
);

/**
 * Returns the list of names for the available regions.
 * Also denotes if it is a private region or not.
 * This is based on the cloud provider selected.
 *
 * @param {object} state - Current state
 * @returns {array} - list of available region names
 */
export const availableRegionsNamesSelector = createSelector(
  availableRegionsSelector,
  selectedCloudProviderSelector,
  (regions, selectedProvider) =>
    regions
      .filter((region) => region.providerName === selectedProvider)
      .map((region) => ({ name: region.name, organization: region.organization })),
);

/**
 * Returns a boolean if a region is private or public
 *
 * @param {object} state - Current state
 * @returns {boolean} - True if a region is private, false otherwise
 */
export const isPrivateRegionSelector = createSelector(
  availableRegionsSelector,
  selectedCloudProviderSelector,
  selectedRegionSelector,
  (regions, selectedProvider, selectedRegion) =>
    Boolean(
      regions
        .filter(
          (region) => region.providerName === selectedProvider && region.name === selectedRegion,
        )
        .map((region) => region.organization)[0],
    ),
);

/**
 * Returns the list of available cloud providers. This is based on:
 * 1) The service that is selected
 * 2) The service subtype that is then automatically selected
 * 3) The regions that the service subtype is available in
 * 4) The providers that those regions are in
 *
 * @param {object} state - Current state
 * @returns {array} - list of available cloud provider objects
 */
export const availableCloudProvidersSelector = createSelector(
  availableRegionsSelector,
  catalogCloudProvidersSelector,
  (regions, providers) => {
    const uniqueProviderNames = new Set(regions.map((region) => region.providerName));
    const availableCloudProviders = providers.filter(
      (provider) => uniqueProviderNames.has(provider.name) && provider.displaySetting === 1,
    );

    return availableCloudProviders;
  },
);

/**
 * Returns the list of names for the available verions of a service
 * name in a component
 *
 * @param {object} state - Current state
 * @param {object} props - Current props of the component using the selector;
 * must contain a serviceName prop
 * @returns {array} - list of available version names
 */
export const versionNamesByServiceSelector = createSelector(
  catalogVersionsSelector,
  (state, props) => props.serviceName,
  (versions, serviceName) =>
    versions
      .filter((version) => version.serviceName === serviceName)
      .map((version) => version.name),
);

/**
 * Returns the list of flavors available based on the following criteria:
 * 1) The flavor is available in the selected region
 * 2) The flavor is available for the selected service subtype
 *
 * @param {object} state - Current state
 * @returns {array} - list of available flavor objects
 */
export const availableFlavorsSelector = createSelector(
  selectedRegionSelector,
  catalogRegionsSelector,
  selectedServiceSubtypeIDSelector,
  catalogFlavorsSelector,
  (selectedRegionName, regions, serviceSubtypeID, flavors) => {
    const [selectedRegion] = regions.filter((region) => region.name === selectedRegionName);
    const potentialFlavorIds = selectedRegion.flavors;

    return flavors.filter(
      (flavor) =>
        flavor.serviceSubtype === serviceSubtypeID &&
        flavor.displaySetting === 1 &&
        potentialFlavorIds.includes(flavor.id),
    );
  },
);

/**
 * Returns the list of addons available for the selected service subtype
 *
 * @param {object} state - Current state
 * @returns {array} - list of available addon objects
 */
export const availableAddonsSelector = createSelector(
  selectedServiceSubtypeIDSelector,
  catalogAddonsSelector,
  (serviceSubtypeID, addons) =>
    addons.filter(
      (addon) => addon.serviceSubtype === serviceSubtypeID && addon.displaySetting === 1,
    ),
);

/**
 * Returns the list of plans available for the selected flavor
 *
 * @param {object} state - Current state
 * @returns {array} - list of available plan objects
 */
export const availablePlansSelector = createSelector(
  (state) => createFormSelector(state, 'flavor'),
  catalogPlansSelector,
  (flavorID, plans) =>
    plans.filter((plan) => plan.flavor === flavorID && plan.displaySetting === 1),
);

/**
 * Returns the list of ACL Kinds for an availble service
 *
 * @param {object} state - Current state
 * @returns {array} - list of available ACL Kinds
 */
export const availableACLKindsSelector = createSelector(
  selectedServiceSelector,
  (selectedService) => getAclKindsByService(selectedService),
);

/**
 * Returns whether or not a MongoDB license warning needs to be displayed during the create process.
 * This state is determined by the following conditions:
 *
 * 1) The user is not at OR ADMIN superuser
 * 2) A user attempts to create a MongoDB instance in a public region
 * 3) That user's org either does not have an ORv4 account or has one that was created after the
 * license agreement cutoff date, June 01, 2019.
 *
 * @package {object} state - Current state
 * @returns {bool} - org's MongoDB eligibility
 */
export const mongoLicenseWarningNeededSelector = createSelector(
  (state) => state.org,
  (state) => state.user,
  selectedServiceSelector,
  selectedRegionSelector,
  isPrivateRegionSelector,
  (orgState, userState, selectedService, selectedRegion, isPrivateRegion) => {
    const { roles } = userState.identity;
    if (roles.includes(OR_ADMIN)) {
      return false;
    }

    if (selectedService !== 'MongoDB') {
      return false;
    }

    if (!selectedRegion || isPrivateRegion) {
      return false;
    }

    const { orv4dtcreated } = orgState;

    if (!orv4dtcreated) {
      return true;
    }

    const MONGODB_ORG_LICENSE_AGREEMENT_CUTOFF_DATETIME = moment.utc('2019-06-01');

    return moment(orv4dtcreated).isAfter(MONGODB_ORG_LICENSE_AGREEMENT_CUTOFF_DATETIME);
  },
);

/**
 * Returns whether or not step one of the create flow is complete
 *
 * @param {object} state - Current state
 * @returns {bool} - status of step one
 */
export const stepOneCompleteSelector = createSelector(
  selectedServiceSelector,
  selectedCloudProviderSelector,
  selectedRegionSelector,
  (service, provider, region) => Boolean(service) && Boolean(provider) && Boolean(region),
);

/**
 * Returns whether or not step two of the create flow is complete
 *
 * @param {object} state - Current state
 * @returns {bool} - status of step two
 */
export const stepTwoCompleteSelector = createSelector(
  selectedFlavorSelector,
  selectedPlanSelector,
  (flavor, plan) => Object.keys(flavor).length > 0 && Object.keys(plan).length > 0,
);

/**
 * Returns whether or not step three of the create flow is complete
 * Checks for CIDR name separately becuase cidrAndName returns true
 * if only the CIDR is entered.
 *
 * @param {object} state - Current state
 * @returns {bool} - status of step three
 */
export const stepThreeCompleteSelector = createSelector(
  selectedCidrSelector,
  (state) => createFormSelector(state, 'cidrName'),
  (cidrAndName, cidrName) => Boolean(cidrAndName) && Boolean(cidrName),
);

/**
 * Returns whether or not step four of the create flow is complete
 *
 * @param {object} state - Current state
 * @returns {bool} - status of step four
 */
export const stepFourCompleteSelector = createSelector(enteredNameSelector, (instanceName) =>
  Boolean(instanceName),
);

/**
 * Returns a list of plan information formatted for display in the PlanSelector in Step Two.
 *
 * @param {Object} state - Current state
 * @returns {{planId:string, sizeInfo:string, nodeInfo:string}[]} - Array of objects:
 * {sizeInfo: string representation of the plan; nodeInfo: information on the node breakdown; planId: id of the plan}
 */
export const availablePlansInfoSelector = createSelector(
  availablePlansSelector,
  selectedServiceSelector,
  (availablePlans, selectedService) => {
    if (!selectedService || !availablePlans.length) {
      return [];
    }
    const availablePlanDisplays = availablePlans.map((availablePlan) => {
      const { sizeInfo, nodeInfo } = createPlanDisplay(selectedService, availablePlan);
      return { planId: availablePlan.id, sizeInfo, nodeInfo };
    });
    return availablePlanDisplays;
  },
);

/**
 * Returns a string representation of size information for a single plan
 *
 * @param {Object} state - Current state
 * @returns {string} - string representation of size information for a single plan
 */
export const planInfoStringSelector = createSelector(
  selectedServiceSelector,
  selectedPlanSelector,
  (service, plan) => {
    if (!service || !plan || !plan.nodes) {
      return '';
    }
    const { sizeInfo } = createPlanDisplay(service, plan);

    return sizeInfo;
  },
);

/**
 * Returns a string representation of node information for a single plan
 *
 * @param {Object} state - Current state
 * @returns {string} - string representation of node information for a single plan
 */
export const nodeInfoStringSelector = createSelector(
  selectedServiceSelector,
  selectedPlanSelector,
  (service, plan) => {
    if (!service || !plan || !plan.nodes) {
      return '';
    }
    const { nodeInfo } = createPlanDisplay(service, plan);

    return nodeInfo;
  },
);

/**
 * Returns a string representation of the user-selected addons for display in Step Two
 *
 * @param {Object} state - Current state
 * @returns {string} - string representation of the selected addons
 */
export const addonInfoStringSelector = createSelector(
  selectedAddonsSelector,
  selectedPlanSelector,
  selectedServiceSelector,
  (addons, plan, selectedService) => {
    let addonInfoString = '';
    if (!addons || !plan || !Object.keys(addons).length) {
      return addonInfoString;
    }

    const planNode = plan.nodes[selectedService.toLowerCase()];

    addons.forEach((addon) => {
      if (addon.name === 'dedicated_master') {
        addonInfoString += ` + 3 × ${addon.value} for master nodes`;
      }
      if (addon.name === 'replicas_for_ha') {
        addonInfoString += ` + ${addon.value} × ${planNode.diskSizeGb}GB for replication`;
      }
      if (addon.name === 'shards') {
        const shardWord = addon.value === 1 ? 'shard' : 'shards';
        // Note that × is a multiplication symbol entity, not the letter x
        addonInfoString += ` × ${addon.value} ${shardWord}`;
      }
    });

    return addonInfoString;
  },
);

/**
 * Returns the id of the selected region, which is currently stored by name in the form state
 *
 * @param {Object} state - Current state
 * @returns {string} - selected region id, or empty string if a region is not yet selected
 */
export const selectedRegionIdSelector = createSelector(
  selectedRegionSelector,
  catalogRegionsSelector,
  (selectedRegionName, regions) => {
    if (!selectedRegionName) {
      return '';
    }

    const [selectedRegionObject] = regions.filter((region) => selectedRegionName === region.name);
    return selectedRegionObject.id;
  },
);

/**
 * Takes the create form state and turns it into the shape needed for submitting to
 * the create endpoint
 *
 * @param {object} state - Current state
 * @returns {object} - body for the create call
 */
export const createInstanceBodySelector = createSelector(
  selectedServiceSelector,
  selectedVersionSelector,
  selectedCloudProviderSelector,
  selectedRegionIdSelector,
  selectedServiceSubtypeSelector,
  selectedPlanSelector,
  selectedAddonsSelector,
  (state) => createFormSelector(state, 'cidr'),
  (state) => createFormSelector(state, 'cidrName'),
  (state) => createFormSelector(state, 'cidrKinds'),
  enteredNameSelector,
  (
    service,
    version,
    provider,
    region,
    serviceSubtype,
    selectedPlan,
    selectedAddons,
    cidr,
    cidrName,
    cidrKinds,
    name,
  ) => {
    const addonsObject = selectedAddons.reduce((acc, cur) => {
      // It appears Infra API chokes if values for HA Replicas are sent as strings,
      // but we still need to handle the case for ES dedicated masters
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(cur.value)) {
        acc[cur.name] = cur.value;
      } else if (typeof cur.value === "boolean") {
        acc[cur.name] = cur.value;
      } else {
        acc[cur.name] = parseInt(cur.value, 10);
      }
      return acc;
    }, {});
    const aclList = cidrKinds
      ? cidrKinds.map((cidrKind) => ({ cidr, name: cidrName, kind: cidrKind }))
      : [];
    const createInstanceBody = {
      service,
      version,
      provider,
      region,
      serviceSubtype,
      plan: selectedPlan.name,
      features: {
        addons: addonsObject,
      },
      settings: {
        system_userlist: {},
      },
      acls: aclList,
      name,
    };
    return createInstanceBody;
  },
);

// END create section

export default getCreateInstanceState;
